import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";

import { useSnackbar } from "notistack";

import { Box } from "@mui/material";

import Layout from "@components/Layout";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { useUserState } from "@context/UserContext";
import { Types } from "@ioc/types";

import { IReadConfirmation, NotificationTabKey } from "../model";
import {
  INotificationCreateDTO,
  INotification,
  INotificationFilter,
  INotificationService,
  INotificationUploadResponse,
  IPaginatedNotifications,
  NotificationStatus,
  ISegmentOption,
} from "@modules/notifications/model/INotifications";

import { ACTIVE } from "../resource/tabs/tabs.header";

interface NotificationContextValue {
  loading: boolean;
  data: IPaginatedNotifications | null;
  currentTab: NotificationTabKey;
  filters: INotificationFilter;
  segments: ISegmentOption[];

  setTab: (tab: NotificationTabKey) => void;

  actions: {
    edit: (uuid: string, payload: INotificationCreateDTO) => Promise<void>;
    create: (payload: INotificationCreateDTO) => Promise<void>;
    getById: (id: string) => Promise<INotification | undefined | null>;
    search: (filters?: Partial<INotificationFilter>) => Promise<void>;

    softDelete: (uuid: string | number) => Promise<void>;
    onReadConfirmation: (uuid: string) => Promise<IReadConfirmation[]>;

    onUploadImage: (file: File) => Promise<INotificationUploadResponse>;
  };
}

const NotificationContext = createContext<NotificationContextValue>(
  {} as NotificationContextValue
);

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider: React.FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const iocContext = useIoCContext();
  const userState = useUserState();

  const notificationService = useMemo(
    () =>
      iocContext.serviceContainer.get<INotificationService>(
        Types.Notifications.INotificationService
      ),
    [iocContext.serviceContainer]
  );

  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState<NotificationTabKey>(ACTIVE);
  const [data, setData] = useState<IPaginatedNotifications | null>(null);
  const [segments, setSegments] = useState<ISegmentOption[]>([]);

  const [filters, setFilters] = useState<INotificationFilter>({
    status: ACTIVE,
    page: 1,
    size: 10,
    retrieveUsers: true,
  });

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await notificationService.getSegments();
        setSegments(
          response.map((item: any) => ({
            value: item.id,
            label: item.desc,
          }))
        );
      } catch (error) {
        console.error("Erro ao buscar segmentos:", error);
      }
    };
    fetchSegments();
  }, [notificationService]);

  const search = useCallback(
    async (extra?: Partial<INotificationFilter>) => {
      setLoading(true);

      try {
        const merged: INotificationFilter = {
          ...filters,
          ...extra,
        };

        const result = await notificationService.searchNotifications(merged);
        setData(result);
        setFilters(merged);
      } catch {
        const errorCreateMessage = "Erro ao buscar notificações";
        enqueueSnackbar(errorCreateMessage, { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    [filters, notificationService, enqueueSnackbar]
  );

  const setTab = useCallback(
    (tab: NotificationTabKey) => {
      setCurrentTab(tab);

      // NOTE: Ao trocar de aba, resetamos página para 1
      const status: NotificationStatus = tab;
      void search({ status, page: 1, retrieveUsers: true });
    },
    [search]
  );

  const create = useCallback(
    async (payload) => {
      try {
        await notificationService.createNotification(payload);
        const successCreateMessage = `Notificação criada com sucesso`;
        enqueueSnackbar(successCreateMessage, { variant: "success" });
      } catch (error) {
        const errorCreateMessage = "Erro ao criar notificação";
        enqueueSnackbar(errorCreateMessage, { variant: "error" });
        throw error;
      }
    },
    [notificationService, enqueueSnackbar]
  );

  const edit = useCallback(
    async (uuid, payload) => {
      try {
        await notificationService.updateNotification(uuid, payload);
        const successEditMessage = "Notificação atualizada com sucesso";
        enqueueSnackbar(successEditMessage, { variant: "success" });
      } catch (error) {
        const errorEditMessage = "Erro ao editar notificação";
        enqueueSnackbar(errorEditMessage, { variant: "error" });
        throw error;
      }
    },
    [notificationService, enqueueSnackbar]
  );

  const getById = useCallback(
    async (uuid) => {
      try {
        return await notificationService.getNotificationById(uuid);
      } catch (error) {
        const errorReadMessage = `Erro ao buscar notificação ${uuid}`;
        enqueueSnackbar(errorReadMessage, { variant: "error" });
      }
    },
    [notificationService, enqueueSnackbar]
  );

  const softDelete = useCallback(
    async (uuid) => {
      try {
        await notificationService.deleteNotification(uuid);
        const successDeleteMessage = `Notificação ${uuid} excluida com sucesso`;
        enqueueSnackbar(successDeleteMessage, { variant: "success" });

        // NOTE: Depois de excluir, recarregar aba atual
        await search({ retrieveUsers: true });
      } catch (error) {
        const errorDeleteMessage = `Erro ao excluir notificaçao ${uuid}`;
        enqueueSnackbar(errorDeleteMessage, { variant: "error" });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notificationService, search, userState.username]
  );

  const onReadConfirmation = useCallback((uuid: string) => {
    try {
      return notificationService.readNotification(uuid);
    } catch (error) {
      const errorReadMessage = `Erro ao buscar notificações lidas`;
      enqueueSnackbar(errorReadMessage, { variant: "error" });
      throw error;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUploadImage = useCallback((file: File) => {
    try {
      return notificationService.uploadNotificationImage(file);
    } catch (error) {
      const errorReadMessage = `Erro ao fazer upload de imagem`;
      enqueueSnackbar(errorReadMessage, { variant: "error" });
      throw error;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: NotificationContextValue = {
    loading,
    data,
    currentTab,
    filters,
    segments,
    setTab,

    actions: {
      create,
      edit,
      getById,
      search,

      softDelete,
      onReadConfirmation,

      onUploadImage,
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      <Layout disableToolbar>
        <Box
          sx={{
            m: { xs: 0.5, md: 0.8, lg: "0px 32px 0px 32px" },
            p: { xs: 0.5, lg: "0px 0px 32px 0px" },
          }}
        >
          {children}
        </Box>
      </Layout>
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }

  return context;
};
